/**
 * @file DownloadReportModal.tsx
 *
 * @description This file provides a dialog interface for police officers to select a preferred language
 * before downloading incident reports.
 *
 * @module Modals
 * @requires React
 * @requires Material-UI
 */
import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Select,
  MenuItem,
  DialogActions,
  Button,
  Typography,
} from "@mui/material";

/**
 * Props interface for the DownloadReportModal component.
 * @interface DownloadReportModalProps
 * @property {boolean} showDownloadLanguageModal - Controls the visibility of the modal dialog.
 * @property {() => void} handleCloseDownloadModal - Callback function to close the modal.
 * @property {(languageCode: string) => Promise<void>} handleDownloadIncident - Async function that handles
 *           the actual downloading process with the selected language code.
 */
interface DownloadReportModalProps {
  showDownloadLanguageModal: boolean;
  handleCloseDownloadModal: () => void;
  handleDownloadIncident: (languageCode: string) => Promise<void>;
}

/**
 * @name DownloadReportModal
 *
 * @desciprtion Modal component that allows police officers to select a language before downloading incident reports.
 * The component handles the language selection and provides UI feedback during download process.
 *
 * @param {DownloadReportModalProps} props - Props for configuring the download modal behavior
 *
 * @returns {JSX.Element} A dialog component with language selection and download options
 *
 * @example
 * <DownloadReportModal
 *   showDownloadLanguageModal={showModal}
 *   handleCloseDownloadModal={() => setShowModal(false)}
 *   handleDownloadIncident={(langCode) => downloadService.fetchReport(incidentId, langCode)}
 * />
 */
export const DownloadReportModal: React.FC<DownloadReportModalProps> = ({
  showDownloadLanguageModal,
  handleCloseDownloadModal,
  handleDownloadIncident,
}) => {
  // State to track the currently selected language code (defaults to English)
  const [selectedLanguageCode, setSelectedLanguageCode] = useState("en");

  // State to track if a download is currently in progress
  const [isDownloading, setIsDownloading] = useState(false);

  /**
   * Handles the download button click event.
   * Sets the downloading state to true, calls the parent download handler,
   * and resets the downloading state when complete.
   */
  const handleDownloadClick = () => {
    setIsDownloading(true);
    handleDownloadIncident(selectedLanguageCode).finally(() => {
      setIsDownloading(false);
    });
  };

  return (
    <Dialog open={showDownloadLanguageModal} onClose={handleCloseDownloadModal}>
      <DialogTitle>
        {/* Instructional text for the user */}
        <Typography>
          {" "}
          Please select a language to download your report.{" "}
        </Typography>
      </DialogTitle>
      <DialogContent sx={{ display: "flex", justifyContent: "center" }}>
        <Select
          fullWidth
          value={selectedLanguageCode}
          onChange={(e) => setSelectedLanguageCode(e.target.value)}
        >
          {/* Available language options - can be expanded as needed */}
          <MenuItem value="en">English</MenuItem>
          <MenuItem value="es">Spanish</MenuItem>
          <MenuItem value="hi">Hindi</MenuItem>
          <MenuItem value="vi">Vietnamese</MenuItem>
        </Select>
      </DialogContent>
      <DialogActions>
        {/* Download button with loading state */}
        <Button onClick={handleCloseDownloadModal}>Cancel</Button>
        <Button
          onClick={handleDownloadClick}
          variant="contained"
          loading={isDownloading}
        >
          Download
        </Button>
      </DialogActions>
    </Dialog>
  );
};
